import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Col, Row } from 'antd';
import { BaseTable } from 'components/UI/BaseTable';
import { CleaningReportExportModal } from 'components/UI/CleaningReportExportModal';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ALL_OPTION, parseParams } from 'hooks/useMachineFilter/utils';
import { useTranslation } from 'react-i18next';
import { TRANSLATION_NAMESPACES } from 'util/translation/constant';
import {
  PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  DATA_EXPORT_FORM_FIELDS,
  OPERATION_STATUS
} from 'util/config/constant';
import { exportCleaningReport } from 'state/global/export/operation';
import * as exportSelector from 'state/global/export/selector';
import { resetDataExportState } from 'state/global/export/action';
import { selectGlobalMyProfile } from 'state/global/profile/selector';
import { MEASUREMENT_FORMAT_STORAGE_KEY } from 'state/global/profile/util/constant';
import { ExpandedArrowIcon } from 'components/UI/ExpandedArrowIcon';
import { CleaningReportExpandable } from '../CleaningReportExpandable';
import { CleaningReportFilter } from '../CleaningReportFilter';
import { RESET_CLEANING_REPORTS_STATE } from './state/action';
import { fetchCleaningReportList } from './state/operation';
import * as selector from './state/selector';
import {
  STATE_PROP,
  DATA_EXPORT_FILTER,
  MAPPING_TITLE_LNG_BY_FILTER
} from './util/constant';

import { renderColumns } from './util/render-columns';
import { DataExportNavigation } from '../DataExportNavigation';
import { RouteImageModal } from '../RouteImageModal';

export const CleaningReportList = () => {
  const { t } = useTranslation(TRANSLATION_NAMESPACES.CLEANING_REPORT);
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const globalMyProfile = useSelector((state) => selectGlobalMyProfile(state));

  const machineObj = useMemo(() => {
    let defaultMachine = {};
    if (location.state) {
      defaultMachine = location.state.defaultMachine;
      return parseParams(defaultMachine.data);
    }
    return parseParams(defaultMachine);
  }, [location.state]);

  const [defaultFilterState, setDefaultFilterState] = useState({
    facility: machineObj.facility,
    object: machineObj.object,
    machineId: machineObj.id !== undefined ? machineObj.id : ALL_OPTION,
    machineIds: machineObj?.id ? [machineObj.id] : [],
    startDate: '',
    endDate: ''
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    stateProp: STATE_PROP
  });

  const { cleaningReportsInfo, dataExportInfo } = useSelector((state) => ({
    cleaningReportsInfo: selector.selectCleaningReportListInfo(state),
    dataExportInfo: exportSelector.selectExportInfo(state)
  }));

  const columns = useMemo(() => {
    return renderColumns({
      history,
      t,
      measurementFormat: globalMyProfile[MEASUREMENT_FORMAT_STORAGE_KEY]
    });
  }, [history, t, globalMyProfile]);

  const handlePageChange = (page) => {
    setPagination((currentPagination) => ({
      ...currentPagination,
      page
    }));
  };

  const handlePageLimit = (limit) => {
    setPagination((currentPagination) => ({
      ...currentPagination,
      page: 1,
      limit
    }));
  };

  const tableProps = useMemo(
    () => ({
      columns,
      loading: cleaningReportsInfo.isLoading,
      dataSource: cleaningReportsInfo.data,
      rowKey: 'id',
      pagination,
      expandable: {
        expandedRowRender: (rowData) => {
          return <CleaningReportExpandable rowData={rowData} />;
        },
        expandIcon: ExpandedArrowIcon,
        expandIconColumnIndex: 8,
        expandRowByClick: true
      },
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      showRowsPerPage: true,
      onPageChange: handlePageChange,
      onLimitChange: handlePageLimit,
      className: 'table-yellow-header'
    }),
    [
      columns,
      cleaningReportsInfo.isLoading,
      cleaningReportsInfo.data,
      pagination
    ]
  );

  const handleChange = useCallback((newFilters) => {
    setDefaultFilterState(newFilters);
  }, []);

  useEffect(() => {
    dispatch(
      fetchCleaningReportList({
        limit: pagination.limit,
        page: pagination.page,
        filters: defaultFilterState
      })
    );
  }, [dispatch, pagination.limit, pagination.page, defaultFilterState]);

  useEffect(
    () => () => {
      dispatch({
        type: RESET_CLEANING_REPORTS_STATE
      });
      dispatch(resetDataExportState());
    },
    [dispatch]
  );

  const [visible, setVisible] = useState(false);
  const dataExportModalRef = useRef({
    title: '',
    filterBy: '',
    initialFormValues: {}
  });

  useEffect(() => {
    if (dataExportInfo.status === OPERATION_STATUS.SUCCESS) {
      setVisible(false);
    }
  }, [dataExportInfo.status]);

  const onOpenDataExportModal = useCallback(
    (fileType, filterBy) => {
      dataExportModalRef.current = {
        title: t(MAPPING_TITLE_LNG_BY_FILTER[filterBy]),
        filterBy,
        initialFormValues: {
          [DATA_EXPORT_FORM_FIELDS.FILE_TYPE]: fileType,
          [DATA_EXPORT_FORM_FIELDS.COLUMNS]: []
        }
      };
      setVisible(true);
    },
    [t]
  );

  const onCloseDataExportModal = useCallback(() => {
    setVisible(false);
    dispatch(resetDataExportState());
  }, [dispatch]);

  const onExportData = useCallback(
    (values) => {
      const { machineIds, startDate, endDate } = defaultFilterState;
      const data = {
        ...values,
        measurementFormat: globalMyProfile[MEASUREMENT_FORMAT_STORAGE_KEY]
      };

      if (
        dataExportModalRef.current.filterBy === DATA_EXPORT_FILTER.CONDITION
      ) {
        data.filters = {
          machineIds,
          startDate,
          endDate
        };
      }

      dispatch(exportCleaningReport(data));
    },
    [defaultFilterState, dispatch, globalMyProfile]
  );

  return (
    <>
      <Row
        gutter={[12, 12]}
        justify="space-between"
        align="bottom"
        className="cleaningReports-header">
        <Col>
          <CleaningReportFilter
            filters={defaultFilterState}
            onChange={handleChange}
          />
        </Col>
        <Col>
          <DataExportNavigation onClick={onOpenDataExportModal} />
        </Col>
      </Row>

      <div className="cleaningReports-table">
        <BaseTable {...tableProps} />
      </div>

      <CleaningReportExportModal
        title={dataExportModalRef.current.title}
        visible={visible}
        onClose={onCloseDataExportModal}
        onFinish={onExportData}
        loading={dataExportInfo.isLoading}
        initialFormValues={dataExportModalRef.current.initialFormValues}
        width={760}
      />

      <RouteImageModal />
    </>
  );
};
